import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, Search, MoreVertical, Pencil, PlaySquare, Eye, EyeOff, Copy, Trash2, Layers, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { RefreshButton } from "@/components/RefreshButton";

import { confirmar } from "@/components/ConfirmDialog";
const errMsg = (e) => formatApiError(e.response?.data?.detail);

const StatusBadge = ({ on }) => (
  <span className={`text-xs font-medium px-2 py-1 rounded border whitespace-nowrap ${on ? "text-green-700 bg-green-50 border-green-200" : "text-slate-600 bg-slate-50 border-slate-200"}`}>
    {on ? "Publicado" : "Rascunho"}
  </span>
);

/** Cursos → Módulos: todos os módulos de todos os cursos; abrir um módulo leva às aulas dele. */
export default function Modules() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [q, setQ] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [dialog, setDialog] = useState(null); // { course_id, id?, title, published, professor_id }
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/admin/modules").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => {
    load();
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/instructors").then((r) => setInstructors(r.data)).catch(() => {});
  }, [load]);

  const shown = useMemo(() => (rows || []).filter((m) =>
    (!courseFilter || m.course_id === courseFilter) &&
    (!q || m.title.toLowerCase().includes(q.toLowerCase()) || m.course_title.toLowerCase().includes(q.toLowerCase()))), [rows, q, courseFilter]);

  const openLessons = (m) => navigate(`/admin/cursos/${m.course_id}#aulas?m=${m.id}`);
  const openNew = () => {
    if (!courses.length) return toast.error("Crie um curso primeiro.");
    const c = courses.find((x) => x.id === courseFilter);
    setDialog({ course_id: courseFilter || "", title: "", published: true, professor_id: c?.coordinator_id || "" });
  };
  const save = async () => {
    if (!dialog.course_id) return toast.error("Escolha o curso do módulo.");
    if (!dialog.title.trim()) return toast.error("Informe o nome do módulo.");
    setSaving(true);
    try {
      const body = { title: dialog.title, published: dialog.published, professor_id: dialog.professor_id };
      if (dialog.id) {
        await api.put(`/courses/${dialog.course_id}/modules/${dialog.id}`, body);
        toast.success("Módulo atualizado.");
        setDialog(null); load();
      } else {
        const r = await api.post(`/courses/${dialog.course_id}/modules`, body);
        const created = r.data.modules[r.data.modules.length - 1];
        toast.success("Módulo criado. Agora cadastre as aulas dele.");
        setDialog(null);
        navigate(`/admin/cursos/${dialog.course_id}#aulas?m=${created.id}&nova=1`);
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };
  const toggle = (m) => act(() => api.put(`/courses/${m.course_id}/modules/${m.id}`, { title: m.title, professor_id: m.professor_id, published: !m.published }),
    m.published ? "Módulo movido para rascunho." : "Módulo publicado.");
  const del = async (m) => {
    if ((await confirmar(`Excluir o módulo "${m.title}" (curso ${m.course_title}) e as ${m.lessons} aula(s) dele?`)))
      act(() => api.delete(`/courses/${m.course_id}/modules/${m.id}`), "Módulo excluído.");
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Módulos</h1>
          <p className="text-slate-500 mt-1">Crie módulos em qualquer curso e organize as aulas por tópicos.</p>
        </div>
        <div className="flex gap-2">
          <RefreshButton onClick={load} />
          <Button onClick={openNew} className="text-white font-semibold" style={{ background: "#1E9E2A" }} data-testid="modules-new"><Plus className="h-4 w-4 mr-1.5" /> Novo módulo</Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-4 flex flex-wrap gap-2 items-center border-b border-slate-100">
          <select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm min-w-[240px]" data-testid="modules-course-filter">
            <option value="">Todos os cursos</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar por módulos..." className="pl-9 h-10" />
          </div>
        </div>

        {rows === null ? (
          <div className="p-4 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded" />)}</div>
        ) : shown.length === 0 ? (
          <div className="p-12 text-center">
            <Layers className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">{rows.length ? "Nenhum módulo com esse filtro." : "Nenhum módulo ainda. Clique em Novo módulo."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto iep-scroll">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50/60">
                <th className="px-4 py-3">Curso</th><th className="px-3 py-3 w-16">Ordem</th><th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Módulo</th><th className="px-3 py-3 hidden md:table-cell">Professor</th>
                <th className="px-3 py-3 hidden sm:table-cell">Aulas</th><th className="px-4 py-3 text-right">Opções</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {shown.map((m) => (
                  <tr key={m.id} data-testid={`module-${m.id}`}>
                    <td className="px-4 py-2.5 text-slate-600 max-w-[220px] truncate" title={m.course_title}>{m.course_title}</td>
                    <td className="px-3 py-2.5 text-slate-600">{m.position}</td>
                    <td className="px-3 py-2.5"><StatusBadge on={m.published} /></td>
                    <td className="px-3 py-2.5"><button onClick={() => openLessons(m)} className="font-medium text-slate-800 hover:text-[#1E9E2A] text-left">{m.title}</button></td>
                    <td className="px-3 py-2.5 hidden md:table-cell text-slate-600">{m.professor_name || "—"}</td>
                    <td className="px-3 py-2.5 hidden sm:table-cell text-slate-600">{m.lessons}</td>
                    <td className="px-4 py-2.5 text-right">
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild><button className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem onSelect={() => openLessons(m)}><PlaySquare className="h-4 w-4 mr-2" /> Aulas / nova aula</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => setDialog({ course_id: m.course_id, id: m.id, title: m.title, published: m.published, professor_id: m.professor_id })}><Pencil className="h-4 w-4 mr-2" /> Editar</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => toggle(m)}>{m.published ? <><EyeOff className="h-4 w-4 mr-2" /> Mover para rascunho</> : <><Eye className="h-4 w-4 mr-2" /> Publicar</>}</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => act(() => api.post(`/courses/${m.course_id}/modules/${m.id}/copy`), "Módulo copiado (como rascunho).")}><Copy className="h-4 w-4 mr-2" /> Copiar</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => del(m)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" /> Excluir</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="px-4 py-3 text-xs text-slate-400 border-t border-slate-100">Total de {shown.length} módulo(s). Clique no módulo para ver e cadastrar as aulas.</p>
          </div>
        )}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{dialog?.id ? "Editar módulo" : "Novo módulo"}</DialogTitle>
            {!dialog?.id && <DialogDescription>Depois de criar, você vai direto para as aulas deste módulo.</DialogDescription>}
          </DialogHeader>
          {dialog && (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5"><Label>Curso *</Label>
                <select value={dialog.course_id} disabled={!!dialog.id}
                  onChange={(e) => { const c = courses.find((x) => x.id === e.target.value); setDialog((p) => ({ ...p, course_id: e.target.value, professor_id: p.professor_id || c?.coordinator_id || "" })); }}
                  className={`h-10 w-full rounded-md border bg-white px-3 text-sm disabled:opacity-60 ${dialog.course_id ? "border-slate-200" : "border-amber-400"}`} data-testid="module-course-select">
                  <option value="">— Escolha o curso —</option>
                  {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select></div>
              <div className="space-y-1.5"><Label>Nome do módulo *</Label>
                <Input autoFocus value={dialog.title} onChange={(e) => setDialog((p) => ({ ...p, title: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && save()} placeholder="Ex.: Módulo 1 - Introdução" data-testid="module-name-input" /></div>
              <div className="space-y-1.5"><Label>Professor(a)</Label>
                <select value={dialog.professor_id} onChange={(e) => setDialog((p) => ({ ...p, professor_id: e.target.value }))} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option value="">Sem professor</option>
                  {instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select></div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                <Label>Publicado (visível aos alunos)</Label>
                <Switch checked={dialog.published} onCheckedChange={(v) => setDialog((p) => ({ ...p, published: v }))} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="text-white" style={{ background: "#1E9E2A" }} data-testid="module-create-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : dialog?.id ? "Salvar" : "Criar e cadastrar aulas"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
